import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from './useDebouncedValue';
import type { PageResponse } from '@/api/envelope';

/** Active column sort (server-side). Null = server default order. */
export type SortState = { columnKey: string; dir: 'asc' | 'desc' } | null;

// URL-state helpers — a hand-edited query string must degrade, never throw.
function parseParam<V>(raw: string | null): V | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as V;
  } catch {
    return null;
  }
}

function isEmptySelection(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value !== 'object') return false;
  return Object.values(value as Record<string, unknown>).every(
    (v) => !Array.isArray(v) || v.length === 0,
  );
}

// Canonical signature — CONTRACTS.md §5. `fetchPage` returns a full PageResponse<T> (already
// unwrapped via selectData); row-mapping is not the hook's job. `sort` is optional — lists that
// don't opt into server sorting simply ignore it.
export interface UseServerTableOptions<T, TFilters = Record<string, string[]>> {
  queryKey: readonly unknown[]; // stable base key; search + filters + sort are appended internally
  fetchPage: (args: {
    pageParam: number; // page index to fetch
    pageSize: number;
    search: string; // 500ms-debounced
    filters: TFilters; // multi-select column filters
    sort: SortState; // active column sort (null = server default)
  }) => Promise<PageResponse<T>>;
  pageSize?: number; // default 20 (parity w/ CMP size=20)
  initialFilters?: TFilters;
  initialSort?: SortState;
  /**
   * `'paged'` fetches exactly one page and exposes page/setPage/totalPage for numbered navigation.
   * `'infinite'` (default) accumulates pages behind an IntersectionObserver sentinel.
   *
   * Paged is the right default for anything the user filters: with infinite scroll a filter that
   * leaves the viewport unfilled keeps re-triggering the sentinel and walks the whole table.
   */
  mode?: 'paged' | 'infinite';
  /**
   * Opt-in URL persistence. When set, search/filters/sort/page are mirrored to
   * `<urlKey>.q|f|s|p` in the query string, so list -> detail -> Back restores the view (and it
   * becomes deep-linkable). Prefixed and opt-in because one page can host several grids.
   */
  urlKey?: string;
}

export function useServerTable<T, TFilters = Record<string, string[]>>({
  queryKey,
  fetchPage,
  pageSize = 20,
  initialFilters,
  initialSort,
  mode = 'infinite',
  urlKey,
}: UseServerTableOptions<T, TFilters>) {
  const [params, setParams] = useSearchParams();
  const paged = mode === 'paged';

  // Seeded once from the URL so a remount after list -> detail -> Back restores the view.
  const [rawSearch, setRawSearch] = useState(() => (urlKey && params.get(`${urlKey}.q`)) || '');
  const search = useDebouncedValue(rawSearch, 500); // 500ms, skips first emission
  const [filters, setFilters] = useState<TFilters>(
    () =>
      (urlKey ? parseParam<TFilters>(params.get(`${urlKey}.f`)) : null) ??
      initialFilters ??
      ({} as TFilters),
  );
  const [sort, setSort] = useState<SortState>(
    () => (urlKey ? parseParam<SortState>(params.get(`${urlKey}.s`)) : null) ?? initialSort ?? null,
  );
  const [page, setPageState] = useState(() => {
    const raw = urlKey ? Number(params.get(`${urlKey}.p`)) : NaN;
    return Number.isInteger(raw) && raw > 0 ? raw : 1;
  });

  // Click a sortable column header: asc -> desc -> off (cleared), matching common data-grid UX.
  const toggleSort = (columnKey: string) => {
    setSort((cur) => {
      if (!cur || cur.columnKey !== columnKey) return { columnKey, dir: 'asc' };
      if (cur.dir === 'asc') return { columnKey, dir: 'desc' };
      return null;
    });
  };

  // Any narrowing of the result set invalidates the current page number — but NOT on mount, which
  // would immediately discard a page restored from the URL.
  const criteriaMounted = useRef(false);
  useEffect(() => {
    if (!criteriaMounted.current) {
      criteriaMounted.current = true;
      return;
    }
    setPageState(1);
  }, [search, filters, sort]);

  const infinite = useInfiniteQuery({
    queryKey: [...queryKey, 'infinite', { search, filters, sort }],
    initialPageParam: 1, // 1-based (verify on wire, §5)
    queryFn: ({ pageParam }) => fetchPage({ pageParam, pageSize, search, filters, sort }),
    getNextPageParam: (last) => {
      const pn = last.pageNumber ?? 0; // 1-based current page
      const tp = last.totalPage ?? 0; // total page count
      return pn < tp ? pn + 1 : undefined; // a next page exists iff current < total
    },
    placeholderData: (prev) => prev, // keepPreviousData — avoid flicker on search/filter change
    enabled: !paged,
  });

  const single = useQuery({
    queryKey: [...queryKey, 'paged', { search, filters, sort, page }],
    queryFn: () => fetchPage({ pageParam: page, pageSize, search, filters, sort }),
    placeholderData: keepPreviousData, // keep the old page visible while the next one loads
    enabled: paged,
  });

  const query = paged ? single : infinite;

  const rows = useMemo<T[]>(() => {
    if (paged) return single.data?.rows ?? [];
    return infinite.data?.pages.flatMap((p) => p.rows ?? []) ?? [];
  }, [paged, single.data, infinite.data]);

  const totalPage = paged
    ? (single.data?.totalPage ?? 0)
    : (infinite.data?.pages[0]?.totalPage ?? 0);

  // totalElements is identical across pages; read it off the first loaded page.
  const total = paged
    ? (single.data?.totalElements ?? 0)
    : (infinite.data?.pages[0]?.totalElements ?? 0);

  const setPage = (next: number) => {
    setPageState(Math.max(1, totalPage > 0 ? Math.min(next, totalPage) : next));
  };

  // Mirror state -> URL so Back restores it. `replace` keeps history clean: the entry the user
  // returns to already carries the final state. Skipped entirely while everything is at its
  // default, so an untouched list never rewrites the address bar.
  useEffect(() => {
    if (!urlKey) return;
    const pristine =
      rawSearch === '' && isEmptySelection(filters) && sort == null && page === 1;
    const hadAny =
      params.has(`${urlKey}.q`) ||
      params.has(`${urlKey}.f`) ||
      params.has(`${urlKey}.s`) ||
      params.has(`${urlKey}.p`);
    if (pristine && !hadAny) return;

    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const write = (key: string, value: string | null) => {
          if (value) next.set(`${urlKey}.${key}`, value);
          else next.delete(`${urlKey}.${key}`);
        };
        write('q', rawSearch || null);
        write('f', isEmptySelection(filters) ? null : JSON.stringify(filters));
        write('s', sort ? JSON.stringify(sort) : null);
        write('p', page > 1 ? String(page) : null);
        return next.toString() === prev.toString() ? prev : next;
      },
      { replace: true },
    );
    // `params`/`setParams` are intentionally omitted: including them re-runs the effect on every
    // URL change (including this one's own write) instead of only when list state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, rawSearch, filters, sort, page]);

  return {
    rows,
    total,
    totalPage,
    page,
    setPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: infinite.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    endReached: paged ? true : !infinite.hasNextPage,
    fetchNextPage: () => {
      if (paged) return;
      if (infinite.hasNextPage && !infinite.isFetchingNextPage) void infinite.fetchNextPage();
    },
    search: rawSearch,
    setSearch: setRawSearch,
    filters,
    setFilters,
    sort,
    toggleSort,
    refetch: query.refetch,
  };
}
